import {
  DownloadIcon,
  PlusIcon,
  TrashIcon,
  UploadIcon,
} from "@heroicons/react/outline";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import RadioButton from "components/radioButton";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import {
  CouponCreateType,
  CouponCustomerInfoType,
  DiscountType,
  GenerateCharacterPosition,
  ICreateCouponForm,
  UserType,
} from "interfaces";
import { isEmpty } from "lodash";
import { useCallback, useState } from "react";
import {
  Controller,
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from "react-hook-form";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  COUPON_CUSTOMER_TYPES,
  GENERATE_CHARACTER_POSITIONS,
  GENERATE_CODE_TYPES,
} from "utills/constants";
import { canAccessByType } from "utills/helpers";
import * as XLSX from "xlsx";

const DEFAULT_CODE_VALUE = { code: "", customer_info_value: null };

export const CouponCode = ({
  codeFieldArrController,
}: {
  codeFieldArrController: UseFieldArrayReturn<
    ICreateCouponForm,
    "coupons.code_info_list",
    "id"
  >;
}) => {
  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ICreateCouponForm>(); // retrieve all hook methods

  const discountTypeWatch = useWatch({
    control: control,
    name: "discount_type_display",
  });

  const couponsWatch = useWatch({
    control: control,
    name: "coupons",
  });
  const createType = couponsWatch?.create_type;
  const customerInfoType = couponsWatch?.customer_info_type;

  const {
    fields: codeFields,
    append: appendCode,
    remove: removeCode,
    replace: replaceCodeFields,
  } = codeFieldArrController;

  const handleAddCode = () => {
    appendCode(DEFAULT_CODE_VALUE);
    trigger("coupons.code_info_list");
  };

  const handleRemoveCode = (index: number) => {
    removeCode(index);
  };

  const handleCustomerInfoToggle = (checked: boolean) => {
    if (checked) {
      setValue("coupons.customer_info_type", CouponCustomerInfoType.CODE);
    } else {
      setValue("coupons.customer_info_type", null);
      // Clear customer info values
      codeFields.forEach((_, index) => {
        setValue(`coupons.code_info_list.${index}.customer_info_value`, null);
      });
      if (createType === CouponCreateType.GENERATE) {
        replaceCodeFields([]);
      }
    }
  };

  const handleImportFile = useCallback(async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) {
          return;
        }
        try {
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length < 2) {
            toast.error("Excel file must have at least 2 sheets");
            return;
          }
          const secondSheet = workbook.Sheets[sheetNames[1]];

          const jsonData = XLSX.utils.sheet_to_json(secondSheet, { header: 1 });
          const voucherData = [];
          const firstRow: any = jsonData[0];
          if (firstRow[0] !== "Coupon Code") {
            return toast.error("Coupon Code not found");
          }
          let customerInfoHeaderType = null;
          if (firstRow[1] === "Customer Code") {
            customerInfoHeaderType = CouponCustomerInfoType.CODE;
          } else if (firstRow[1] === "Customer Email") {
            customerInfoHeaderType = CouponCustomerInfoType.EMAIL;
          } else {
            if (!isEmpty(firstRow[1])) {
              return toast.error("Invalid Customer Info");
            }
          }
          for (let i = 1; i < jsonData.length; i++) {
            const row: any = jsonData[i];
            const code = row[0]; // Column A
            const info = customerInfoHeaderType ? row[1] : null; // Column B
            if (info && customerInfoHeaderType) {
              setValue("coupons.customer_info_type", customerInfoHeaderType);
            }
            if (createType === CouponCreateType.MANUAL) {
              if (code) {
                voucherData.push({
                  code: String(code).trim(),
                  customer_info_value: info ? String(info).trim() : null,
                });
              }
            } else if (createType === CouponCreateType.GENERATE) {
              voucherData.push({
                code: null,
                customer_info_value: info ? String(info).trim() : null,
              });
            }
          }

          if (voucherData.length > 0) {
            setValue("coupons.code_info_list", voucherData);
            toast.success(
              `Successfully loaded ${voucherData.length} voucher from Excel file`
            );
          } else {
            toast.error("Coupon code not found");
          }
        } catch (err: any) {
          toast.error("Error while reading file: " + err.message);
        }
      };

      input.click();
    } catch (err: any) {
      toast.error("Error: " + err.message);
    }
  }, [createType]);

  const handleImportWPSFile = useCallback(async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) {
          return;
        }
        try {
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length < 1) {
            toast.error("Excel file must have at least 1 sheet");
            return;
          }
          const firstSheet = workbook.Sheets[sheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          const voucherData = [];
          const firstRow: any = jsonData[0];
          if (firstRow[0] !== "Coupon Code") {
            return toast.error("Coupon Code not found");
          }
          for (let i = 1; i < jsonData.length; i++) {
            const row: any = jsonData[i];
            const code = row[0]; // Column A
            if (createType === CouponCreateType.MANUAL) {
              if (code) {
                voucherData.push({
                  code: String(code).trim(),
                  customer_info_value: null,
                });
              }
            }
          }

          if (voucherData.length > 0) {
            setValue("coupons.code_info_list", voucherData);
            toast.success(
              `Successfully loaded ${voucherData.length} voucher from Excel file`
            );
          } else {
            toast.error("Error has occurred");
          }
        } catch (err: any) {
          toast.error("Error while reading file: " + err.message);
        }
      };

      input.click();
    } catch (err: any) {
      toast.error("Error: " + err.message);
    }
  }, [createType]);

  const handleCreateTypeChange = (newType: CouponCreateType) => {
    setValue("coupons.create_type", newType);
    if (newType === CouponCreateType.MANUAL) {
      setValue("coupons.generate_character_position", null);
      setValue("coupons.generate_characters", null);
      setValue("coupons.generate_character_length", null);
      setValue("coupons.generate_total_coupons", null);
    } else if (newType === CouponCreateType.GENERATE) {
      replaceCodeFields([]);
      setValue("coupons.customer_info_type", null);
      setValue(
        "coupons.generate_character_position",
        GenerateCharacterPosition.PREFIX
      );
    }
  };

  const shouldShowCouponsSection = () => {
    const discountTypesWithCouponsSection = [
      DiscountType.PERCENT,
      DiscountType.FIX_PRICE,
    ];
    return discountTypesWithCouponsSection.includes(discountTypeWatch);
  };

  console.log(errors);

  return shouldShowCouponsSection() ? (
    <div className="box-container">
      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-8 text-sm">
        <div className="col-span-full flex items-center justify-between">
          <p className="font-bold text-base">Coupon Code</p>
          <Controller
            control={control}
            name="coupons.is_active"
            render={({ field: { onChange, value = true, ref } }) => (
              <SwitchButton checked={value} onChange={onChange} />
            )}
          />
        </div>
        <div className="col-span-full">
          <Label required id="max_time_to_use" name="Max Time To Use" />
          <Input
            id="coupons.max_times_used"
            type="number"
            error={errors["coupons"]?.max_times_used}
            placeholder="Max Time To Use"
            {...register("coupons.max_times_used", {
              valueAsNumber: true,
            })}
          />
          {errors["coupons"]?.max_times_used ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["coupons"]?.max_times_used.message}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-4">
          <Label required id="coupon_code" name="Coupon Code" />
          <div className="flex flex-row flex-wrap gap-x-3">
            {GENERATE_CODE_TYPES.map((type) => (
              <RadioButton
                key={type.value}
                checked={type.value === createType}
                onChange={() => {
                  handleCreateTypeChange(type.value);
                }}
                item={{
                  id: type.value,
                  title: type.label,
                }}
              />
            ))}
          </div>
          {errors["coupons"]?.create_type ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["coupons"]?.create_type.message}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-4">
          {canAccessByType(UserType.ADMIN) && (
            <Input
              type="checkbox"
              label="Customer info"
              id="customerInfo"
              checked={customerInfoType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCustomerInfoToggle(e.target.checked)
              }
            />
          )}
        </div>
        <div className="col-span-full">
          {/* Generate Code Section */}
          {createType === CouponCreateType.GENERATE ? (
            <div className="grid gap-x-4 gap-y-4 sm:grid-cols-8 text-sm">
              <div className="col-span-full sm:col-span-2">
                <Label
                  required
                  id="coupons.generate_character_position"
                  name="Fixed Text Position"
                />
                <Controller
                  control={control}
                  name="coupons.generate_character_position"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="coupons.generate_character_position"
                        inputRef={ref}
                        options={GENERATE_CHARACTER_POSITIONS}
                        id="coupons.generate_character_position"
                        placeholder="Fixed Text Position"
                        value={
                          GENERATE_CHARACTER_POSITIONS.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={errors["coupons"]?.generate_character_position}
                      />
                    );
                  }}
                />
                {errors["coupons"]?.generate_character_position ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["coupons"]?.generate_character_position.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full sm:col-span-2">
                <Label
                  required
                  id="coupons.generate_characters"
                  name="Fixed Text"
                />
                <Input
                  id="coupons.generate_characters"
                  type="text"
                  className="bg-white"
                  error={errors["coupons"]?.generate_characters}
                  placeholder="Fixed Text"
                  maxLength={18}
                  {...register("coupons.generate_characters")}
                />
                {errors["coupons"]?.generate_characters ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["coupons"]?.generate_characters.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full sm:col-span-2">
                <Label
                  required
                  id="coupons.generate_character_length"
                  name="Number of Random Code Characters"
                />
                <Input
                  id="coupons.generate_character_length"
                  type="number"
                  className="bg-white"
                  error={errors["coupons"]?.generate_character_length}
                  placeholder="Number of Random Code Characters"
                  min={2}
                  max={18}
                  {...register("coupons.generate_character_length", {
                    valueAsNumber: true,
                  })}
                />
                {errors["coupons"]?.generate_character_length ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["coupons"]?.generate_character_length.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full sm:col-span-2">
                <Label
                  required
                  id="coupons.generate_total_coupons"
                  name="Total Number of Random Coupon Code"
                />
                <Input
                  id="coupons.generate_total_coupons"
                  type="number"
                  className="bg-white"
                  error={errors["coupons"]?.generate_total_coupons}
                  placeholder="Total Number of Random Coupon Code"
                  min={1}
                  max={3000}
                  {...register("coupons.generate_total_coupons", {
                    valueAsNumber: true,
                  })}
                />
                {errors["coupons"]?.generate_total_coupons ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["coupons"]?.generate_total_coupons.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <></>
          )}
          {createType === CouponCreateType.MANUAL ||
          (createType === CouponCreateType.GENERATE &&
            customerInfoType &&
            canAccessByType(UserType.ADMIN)) ? (
            <div>
              <div className="flex items-center gap-x-2.5 max-w-full sm:w-96 mt-4">
                <Link
                  to={
                    canAccessByType(UserType.ADMIN)
                      ? "/files/import_coupon_codes.xlsx"
                      : "/files/import_coupons.xlsx"
                  }
                  className="flex-1 max-w-full"
                  target="_blank"
                  download
                >
                  <Button
                    type="button"
                    className="h-10 px-0 w-full max-w-full justify-center bg-white text-brand-500 ring-1 ring-inset ring-magnolia-700 hover:bg-magnolia-400 active:bg-magnolia-500 focus:ring-bg-magnolia-500"
                  >
                    <DownloadIcon className="size-4 mr-2" /> Download Template
                  </Button>
                </Link>
                <Button
                  type="button"
                  onClick={
                    canAccessByType(UserType.ADMIN)
                      ? () => handleImportFile()
                      : () => handleImportWPSFile()
                  }
                  className="flex-1 h-10 px-0 max-w-full justify-center bg-white text-brand-500 ring-1 ring-inset ring-magnolia-700 hover:bg-magnolia-400 active:bg-magnolia-500 focus:ring-bg-magnolia-500"
                >
                  <UploadIcon className="size-4 mr-2" /> Import Code File
                </Button>
              </div>
              <div className="flex flex-col gap-y-4 col-span-full mt-4">
                <div className="px-4 py-3 bg-magnolia-500 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Coupon Code</p>
                      <p className="text-xs">
                        Letters and numbers only, 20 characters max. You can
                        paste a list from Excel.
                      </p>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleAddCode}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <PlusIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                  {customerInfoType ? (
                    <div className="mt-2">
                      <Label
                        required
                        id="coupons.customer_info_type"
                        name="Customer info"
                      />
                      <Controller
                        control={control}
                        name="coupons.customer_info_type"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <Select
                              key="coupons.customer_info_type"
                              inputRef={ref}
                              options={COUPON_CUSTOMER_TYPES}
                              id="coupons.customer_info_type"
                              placeholder="Customer info"
                              value={
                                COUPON_CUSTOMER_TYPES.find(
                                  (selected: { label: string; value: any }) =>
                                    selected.value === value
                                ) ?? ""
                              }
                              onChange={(selected: {
                                label: string;
                                value: any;
                              }) => {
                                onChange(selected?.value);
                              }}
                              error={errors["coupons"]?.customer_info_type}
                            />
                          );
                        }}
                      />
                      {errors["coupons"]?.customer_info_type ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["coupons"]?.customer_info_type.message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                {!isEmpty(codeFields) ? (
                  <div className="bg-white flex flex-col gap-y-4 max-h-96 overflow-y-auto">
                    {codeFields.map((field, index) => (
                      <div key={field.id} className="flex items-start gap-x-4">
                        <div className="shrink-0 py-3">{index + 1}</div>
                        {!(createType === CouponCreateType.GENERATE) && (
                          <div className="flex-1">
                            <Input
                              id={`coupons.code_info_list.${index}.code`}
                              type="text"
                              className="bg-white"
                              error={
                                errors["coupons"]?.code_info_list?.[index]?.code
                              }
                              placeholder="Enter coupon code"
                              {...register(
                                `coupons.code_info_list.${index}.code`, {
                                  onChange: (event) => {
                                    const isPasted = event.nativeEvent.inputType.startsWith('insertFromPaste');
                                    if (isPasted && watch("coupons.code_info_list").length < 2) {
                                      const codeArr = (event.target.value ?? "").split(" ");
                                      replaceCodeFields(codeArr.map((code: string) => ({code: code, customer_info_value: null})));
                                    }
                                  }
                                }
                              )}
                            />
                            {errors["coupons"]?.code_info_list?.[index]
                              ?.code ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {
                                    errors["coupons"]?.code_info_list?.[index]
                                      ?.code?.message
                                  }
                                </span>
                              </p>
                            ) : null}
                          </div>
                        )}
                        {customerInfoType && (
                          <div className="flex-1">
                            <Input
                              id={`coupons.code_info_list.${index}.customer_info_value`}
                              className="bg-white"
                              placeholder={`Enter ${
                                customerInfoType ===
                                CouponCustomerInfoType.EMAIL
                                  ? "email"
                                  : customerInfoType ===
                                    CouponCustomerInfoType.CODE
                                  ? "code"
                                  : ""
                              }`}
                              error={
                                errors["coupons"]?.code_info_list?.[index]
                                  ?.customer_info_value
                              }
                              {...register(
                                `coupons.code_info_list.${index}.customer_info_value`
                              )}
                            />
                            {errors["coupons"]?.code_info_list?.[index]
                              ?.customer_info_value ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {
                                    errors["coupons"]?.code_info_list?.[index]
                                      ?.customer_info_value?.message
                                  }
                                </span>
                              </p>
                            ) : null}
                          </div>
                        )}
                        {(createType === CouponCreateType.MANUAL
                          ? codeFields.length > 1
                          : true) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCode(index)}
                            className="mt-3 text-red-600 hover:bg-red-50 rounded"
                          >
                            <TrashIcon className="size-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {errors["coupons"]?.code_info_list?.root ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["coupons"]?.code_info_list?.root?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
          {errors["coupons"]?.root ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["coupons"]?.root.message}
              </span>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};
